export interface HeadingData {
  bearingDeg: number;
  source: 'trajectory' | 'waiting' | null;
  gpsHz: number;
}

export interface HeadingOptions {
  onHeading: (data: HeadingData) => void;
  minSpeedMps?: number;
  trajectoryWindowMs?: number; // Fenêtre de temps pour calculer la trajectoire
}

interface GpsPoint {
  lat: number;
  lon: number;
  timestamp: number;
}

class HeadingManager {
  private options: Required<HeadingOptions>;
  private gpsHistory: GpsPoint[] = [];
  private gpsTimestamps: number[] = [];
  private currentHeading: HeadingData = {
    bearingDeg: 0,
    source: null,
    gpsHz: 0
  };

  constructor(options: HeadingOptions) {
    this.options = {
      minSpeedMps: 0.5, // 1.8 km/h minimum pour calculer une trajectoire (plus sensible)
      trajectoryWindowMs: 4000, // Fenêtre de 4 secondes
      ...options
    };
  }

  // Plus besoin de boussole, on supprime toutes ces méthodes
  async requestCompassPermission(): Promise<boolean> {
    // Méthode gardée pour compatibilité mais ne fait rien
    return false;
  }

  updateFromGps(position: { lat: number; lon: number; speed: number | null; timestamp: number }) {
    this.updateGpsHz(position.timestamp);
    
    // Ajouter le point GPS à l'historique
    const newPoint: GpsPoint = {
      lat: position.lat,
      lon: position.lon,
      timestamp: position.timestamp
    };
    
    this.gpsHistory.push(newPoint);
    
    // Garder seulement les points dans la fenêtre de temps
    const cutoffTime = position.timestamp - this.options.trajectoryWindowMs;
    this.gpsHistory = this.gpsHistory.filter(p => p.timestamp > cutoffTime);
    
    // Calculer la trajectoire si on a assez de points et qu'on bouge
    const speed = position.speed || 0;
    
    if (this.gpsHistory.length >= 2 && speed >= this.options.minSpeedMps) {
      const bearing = this.calculateTrajectoryBearing();
      if (bearing !== null) {
        this.currentHeading.bearingDeg = bearing;
        this.currentHeading.source = 'trajectory';
      } else {
        this.currentHeading.source = 'waiting';
      }
    } else {
      // Pas assez de vitesse ou de points
      this.currentHeading.source = 'waiting';
    }
    
    this.options.onHeading(this.currentHeading);
  }

  private updateGpsHz(timestamp: number) {
    this.gpsTimestamps.push(timestamp);
    
    const tenSecondsAgo = timestamp - 10000;
    this.gpsTimestamps = this.gpsTimestamps.filter(t => t > tenSecondsAgo);
    
    if (this.gpsTimestamps.length > 1) {
      const duration = (this.gpsTimestamps[this.gpsTimestamps.length - 1] - this.gpsTimestamps[0]) / 1000;
      this.currentHeading.gpsHz = duration > 0 ? (this.gpsTimestamps.length - 1) / duration : 0;
    }
  }

  private calculateTrajectoryBearing(): number | null {
    if (this.gpsHistory.length < 2) return null;
    
    // Prendre le point le plus ancien et le plus récent pour une trajectoire stable
    const oldestPoint = this.gpsHistory[0];
    const newestPoint = this.gpsHistory[this.gpsHistory.length - 1];
    
    // Calculer la distance pour filtrer le bruit GPS
    const distance = this.haversineDistance(oldestPoint, newestPoint);
    
    // Si on a bougé de moins de 5 mètres, considérer qu'on est immobile
    if (distance < 5) {
      return this.currentHeading.bearingDeg; // Garder le bearing actuel
    }
    
    // Calculer le bearing entre les deux points
    const bearing = this.calculateBearing(oldestPoint, newestPoint);
    
    console.log('🧭 Calcul trajectoire:', {
      from: `${oldestPoint.lat.toFixed(6)}, ${oldestPoint.lon.toFixed(6)}`,
      to: `${newestPoint.lat.toFixed(6)}, ${newestPoint.lon.toFixed(6)}`,
      distance: `${distance.toFixed(1)}m`,
      rawBearing: `${bearing.toFixed(1)}°`,
      oldBearing: `${this.currentHeading.bearingDeg.toFixed(1)}°`
    });
    
    // Lissage plus fort pour éviter les rotations brusques
    if (this.currentHeading.bearingDeg !== 0) {
      const delta = this.shortestAngularDistance(this.currentHeading.bearingDeg, bearing);
      // Réduire le facteur de lissage de 0.2 à 0.1 pour des transitions plus douces
      const smoothedBearing = this.normalizeAngle(this.currentHeading.bearingDeg + delta * 0.1);
      console.log('🎯 Bearing lissé:', `${smoothedBearing.toFixed(1)}°`);
      return smoothedBearing;
    }
    
    console.log('🎯 Premier bearing:', `${bearing.toFixed(1)}°`);
    return bearing;
  }
  
  private haversineDistance(p1: GpsPoint, p2: GpsPoint): number {
    const R = 6371000; // Rayon de la Terre en mètres
    const lat1 = p1.lat * Math.PI / 180;
    const lat2 = p2.lat * Math.PI / 180;
    const deltaLat = (p2.lat - p1.lat) * Math.PI / 180;
    const deltaLon = (p2.lon - p1.lon) * Math.PI / 180;
    
    const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLon/2) * Math.sin(deltaLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    
    return R * c;
  }
  
  private calculateBearing(from: GpsPoint, to: GpsPoint): number {
    const lat1 = from.lat * Math.PI / 180;
    const lat2 = to.lat * Math.PI / 180;
    const deltaLon = (to.lon - from.lon) * Math.PI / 180;
    
    const x = Math.sin(deltaLon) * Math.cos(lat2);
    const y = Math.cos(lat1) * Math.sin(lat2) -
              Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);
    
    const bearing = Math.atan2(x, y) * 180 / Math.PI;
    return this.normalizeAngle(bearing);
  }

  private normalizeAngle(angle: number): number {
    while (angle < 0) angle += 360;
    while (angle >= 360) angle -= 360;
    return angle;
  }

  private shortestAngularDistance(from: number, to: number): number {
    const diff = to - from;
    if (diff > 180) return diff - 360;
    if (diff < -180) return diff + 360;
    return diff;
  }

  getCurrentHeading(): HeadingData {
    return { ...this.currentHeading };
  }

  destroy() {
    // Nettoyer l'historique et réinitialiser le bearing
    this.gpsHistory = [];
    this.gpsTimestamps = [];
    // Réinitialiser le bearing pour la prochaine simulation
    this.currentHeading.bearingDeg = 0;
  }
}

let headingManagerInstance: HeadingManager | null = null;

export function startHeadingTracking(options: HeadingOptions): HeadingManager {
  if (headingManagerInstance) {
    headingManagerInstance.destroy();
  }
  headingManagerInstance = new HeadingManager(options);
  return headingManagerInstance;
}

export function getHeadingManager(): HeadingManager | null {
  return headingManagerInstance;
}